﻿import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Eye, Pencil, Plus } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { financeService, type Vendor, type VendorDetail } from '@/services/finance.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { formatINR } from '@/lib/currency';
import { VendorFormDialog } from './components/VendorFormDialog';

function isOverLimit(v: Vendor): boolean {
  const limit = Number(v.credit_limit);
  return limit > 0 && Number(v.outstanding) > limit;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/40 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || '—'}</span>
    </div>
  );
}

export function VendorsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 15;
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const [formState, setFormState] = useState<{ mode: 'create' | 'edit'; vendor?: VendorDetail } | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const params = { page, limit, q: debouncedQ || undefined };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['finance', 'vendors', params],
    queryFn: () => financeService.listVendors(params),
  });

  const viewQuery = useQuery({
    queryKey: ['finance', 'vendor', viewId],
    queryFn: () => financeService.getVendor(viewId!),
    enabled: !!viewId,
  });

  const openEdit = async (id: string) => {
    const detail = await financeService.getVendor(id);
    setFormState({ mode: 'edit', vendor: detail });
  };

  const columns: Column<Vendor>[] = [
    { key: 'name', header: 'Vendor Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'type', header: 'Type', cell: (r) => <span className="text-muted-foreground">{r.type}</span> },
    { key: 'total_purchase', header: 'Total Purchase', className: 'text-right', cell: (r) => <span className="tabular-nums">{formatINR(r.total_purchase)}</span> },
    { key: 'total_paid', header: 'Total Paid', className: 'text-right', cell: (r) => <span className="tabular-nums">{formatINR(r.total_paid)}</span> },
    {
      key: 'outstanding', header: 'Outstanding', className: 'text-right',
      cell: (r) => (
        <span className={`inline-flex items-center justify-end gap-1 tabular-nums ${isOverLimit(r) ? 'font-semibold text-rose-600' : ''}`}>
          {isOverLimit(r) && <AlertTriangle className="h-3.5 w-3.5" />}
          {formatINR(r.outstanding)}
        </span>
      ),
    },
    { key: 'credit_limit', header: 'Credit Limit', className: 'text-right', cell: (r) => <span className="tabular-nums">{formatINR(r.credit_limit)}</span> },
    { key: 'status', header: 'Status', cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      key: 'actions', header: 'Action', className: 'w-24',
      cell: (r) => (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => setViewId(r.id)} title="View">
            <Eye className="h-5 w-5" />
          </Button>
          <PermissionGate permission={PERMISSIONS.FINANCE_VENDORS_EDIT}>
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => void openEdit(r.id)} title="Edit">
              <Pencil className="h-5 w-5" />
            </Button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  const pagination = data
    ? { page: data.pagination.page, totalPages: data.pagination.totalPages, total: data.pagination.total, onPageChange: setPage }
    : undefined;

  const v = viewQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Vendor Management</h1>
          <p className="text-sm text-muted-foreground">Manage vendors and their financial profiles</p>
        </div>
        <PermissionGate permission={PERMISSIONS.FINANCE_VENDORS_CREATE}>
          <Button onClick={() => setFormState({ mode: 'create' })}>
            <Plus className="h-4 w-4" /> Add Vendor
          </Button>
        </PermissionGate>
      </div>

      <SearchInput
        value={q}
        onChange={setQ}
        placeholder="Search vendors…"
        containerClassName="w-full sm:max-w-xs"
      />

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(r) => r.id}
        loading={isLoading}
        error={isError}
        onRetry={() => void queryClient.invalidateQueries({ queryKey: ['finance', 'vendors'] })}
        empty="No vendors yet. Add your first vendor to get started."
        pagination={pagination}
      />

      {formState && (
        <VendorFormDialog
          open
          mode={formState.mode}
          vendor={formState.vendor}
          onClose={() => setFormState(null)}
          onSuccess={() => setFormState(null)}
        />
      )}

      <Dialog open={!!viewId} onOpenChange={(o) => !o && setViewId(null)}>
        <DialogContent className="w-full max-w-md">
          <DialogHeader>
            <DialogTitle>{v?.name ?? 'Vendor'}</DialogTitle>
          </DialogHeader>
          {viewQuery.isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : v ? (
            <div className="space-y-0.5">
              <DetailRow label="Type" value={v.type} />
              <DetailRow label="Contact Person" value={v.contact_person} />
              <DetailRow label="Mobile" value={v.mobile} />
              <DetailRow label="Email" value={v.email} />
              <DetailRow label="GST Number" value={v.gst_number} />
              <DetailRow label="PAN" value={v.pan} />
              <DetailRow label="Bank Account" value={v.bank_account_number ? `${v.bank_account_name ?? ''} · ${v.bank_account_number}` : null} />
              <DetailRow label="IFSC" value={v.ifsc} />
              <DetailRow label="UPI ID" value={v.upi_id} />
              <DetailRow label="Credit Limit" value={formatINR(v.credit_limit)} />
              <DetailRow label="Total Purchase" value={formatINR(v.total_purchase)} />
              <DetailRow label="Total Paid" value={formatINR(v.total_paid)} />
              <DetailRow label="Outstanding" value={formatINR(v.outstanding)} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
